import { useCallback, useEffect, useRef, useState } from "react";
import { Camera, ImageOff, Loader2, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { errMsg, useApi } from "@/lib/api";

export const uploadPhotos = async (api, deliveryId, files) => {
  let ok = 0;
  for (const file of files) {
    const fd = new FormData();
    fd.append("file", file);
    try { await api.post(`/deliveries/${deliveryId}/photos`, fd); ok += 1; }
    catch (e) { toast.error(`${file.name}: ${errMsg(e)}`); }
  }
  return ok;
};

const AuthImage = ({ id, className, onClick, testId }) => {
  const api = useApi();
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let obj;
    api.get(`/files/${id}`, { responseType: "blob" })
      .then((r) => { obj = URL.createObjectURL(r.data); setUrl(obj); })
      .catch(() => setFailed(true));
    return () => { if (obj) URL.revokeObjectURL(obj); };
  }, [api, id]);
  if (failed) return <div className={`flex items-center justify-center bg-slate-100 ${className}`}><ImageOff className="h-5 w-5 text-slate-400" /></div>;
  if (!url) return <div className={`flex items-center justify-center bg-slate-50 ${className}`}><Loader2 className="h-5 w-5 animate-spin text-orange-600" /></div>;
  return <img src={url} alt="Wiegeschein" className={className} onClick={onClick} data-testid={testId} />;
};

export default function PhotosDialog({ delivery, canEdit, onClose, onChanged }) {
  const api = useApi();
  const input = useRef(null);
  const [photos, setPhotos] = useState(null);
  const [big, setBig] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get(`/deliveries/${delivery.id}/photos`).then((r) => setPhotos(r.data)), [api, delivery.id]);
  useEffect(() => { load(); }, [load]);

  const add = async (files) => {
    if (!files.length) return;
    setBusy(true);
    const ok = await uploadPhotos(api, delivery.id, files);
    if (ok) toast.success(ok === 1 ? "Foto gespeichert" : `${ok} Fotos gespeichert`);
    await load();
    onChanged();
    setBusy(false);
  };
  const remove = async (p) => {
    try { await api.delete(`/files/${p.id}`); toast.success("Foto entfernt"); setBig(null); load(); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl border-slate-200 bg-white" data-testid="dialog-delivery-photos">
        <DialogHeader>
          <DialogTitle>Wiegeschein – {delivery.yard_name}, {new Date(`${delivery.date}T00:00:00`).toLocaleDateString("de-DE")}</DialogTitle>
        </DialogHeader>
        {big ? (
          <div className="space-y-3">
            <AuthImage id={big.id} className="max-h-[65vh] w-full rounded-lg object-contain" testId="photo-large" />
            <div className="flex justify-between">
              <Button variant="secondary" onClick={() => setBig(null)} data-testid="btn-photo-back"><X className="mr-2 h-4 w-4" /> Zurück</Button>
              {canEdit && <Button variant="ghost" onClick={() => remove(big)} data-testid="btn-photo-delete"><Trash2 className="mr-2 h-4 w-4 text-red-600" /> Foto entfernen</Button>}
            </div>
          </div>
        ) : !photos ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-orange-600" /></div>
        ) : (
          <div className="space-y-4">
            {photos.length === 0 && <p className="py-6 text-center text-sm text-slate-500" data-testid="photos-empty">Noch kein Foto hinterlegt.</p>}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {photos.map((p) => (
                <AuthImage key={p.id} id={p.id} onClick={() => setBig(p)} className="aspect-[3/4] w-full cursor-pointer rounded-lg border border-slate-200 object-cover transition-transform hover:scale-[1.02]" testId={`photo-thumb-${p.id}`} />
              ))}
            </div>
            {canEdit && (
              <>
                <input ref={input} type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={(e) => { add([...e.target.files]); e.target.value = ""; }} data-testid="input-photo-file" />
                <Button onClick={() => input.current?.click()} disabled={busy} data-testid="btn-photo-add">
                  {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Camera className="mr-2 h-4 w-4" />} Foto aufnehmen / hochladen
                </Button>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
